import cloneDeep from 'lodash/cloneDeep';

/**
 * Mobile Web version of the PromiseCache from DomoWeb
 * Great for caching the result of HTTP requests.
 * Will temporarily cache the promise in case new requests are made before the initial one finishes.
 * Note: does not cache error results.
 */

class BasePromiseCache {
  constructor(resolveFunction) {
    this.promiseCache = {};
    this.resultCache = {};
    this.resolveFunction = resolveFunction;
  }

  has(key) {
    return !!(this.resultCache[key] || this.promiseCache[key]);
  }

  retrieve(key) {
    if (!this.has(key)) {
      return this.resolveFunction();
    }
    if (this.resultCache[key]) {
      return this.resolveFunction(cloneDeep(this.resultCache[key]));
    } else if (this.promiseCache[key]) {
      return this.promiseCache[key];
    }
  }

  store(key, promise) {
    this.promiseCache[key] = promise;
    promise.then((result) => {
      // remove the promise from the cache
      this.remove(key);
      // cache the result
      this.resultCache[key] = cloneDeep(result);
    }, () => {
      // we don't want to cache it if the result is an error
      this.remove(key);
    });
  }

  remove(key) {
    delete this.promiseCache[key];
    delete this.resultCache[key];
  }

  clear() {
    this.promiseCache = {};
    this.resultCache = {};
  }

  keys() {
    return Object.keys({ ...this.promiseCache, ...this.resultCache });
  }

  useCache(key, fetchMethod) {
    if (this.has(key)) {
      return this.retrieve(key);
    } else {
      const promise = fetchMethod();
      this.store(key, promise);
      return promise;
    }
  }
}

class PromiseCache extends BasePromiseCache {
  constructor(config) {
    super((value) => Promise.resolve(value), config);
  }
}

export default PromiseCache;
